'use client';

import { VStack } from '@terra/ui-styled-system';
import {
  Field,
  LabelDefault,
  SegmentGroup,
  Select,
  createSelectListCollection,
  Tooltip,
} from '@terra/ui-design-system';
import { ArrowUpDownIcon, Tick01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import {
  useMemo,
  useCallback,
  type Dispatch,
  type SetStateAction,
} from 'react';
import {
  MATCH_REPLACE_ORIGINS,
  type MatchReplaceOrigin,
} from '../../store/proxy-store';
import {
  MATCH_REPLACE_ORIGIN_LABEL_KEYS,
  RULE_EDITOR_CONTROL_HEIGHT,
  ensureValidMatchType,
  type RuleFormMatchLocation,
  type RuleFormType,
} from './match-replace-rule-form';

type RuleWhereSectionProps = {
  ruleForm: RuleFormType;
  setRuleForm: Dispatch<SetStateAction<RuleFormType>>;
};

const ORIGIN_TOOLTIP_KEYS: Record<MatchReplaceOrigin, string> = {
  proxy: 'applyToProxyTooltip',
  coder: 'applyToCoderTooltip',
  voyager: 'applyToVoyagerTooltip',
};

export function RuleWhereSection({
  ruleForm,
  setRuleForm,
}: RuleWhereSectionProps) {
  const tRuleDialog = useTranslations('matchReplace.ruleDialog');

  const originCollection = useMemo(
    () =>
      createSelectListCollection({
        items: MATCH_REPLACE_ORIGINS.map((origin) => ({
          label: tRuleDialog(MATCH_REPLACE_ORIGIN_LABEL_KEYS[origin]),
          value: origin,
        })),
      }),
    [tRuleDialog],
  );

  const selectedOriginsLabel = ruleForm.applyTo
    .map((origin) => tRuleDialog(MATCH_REPLACE_ORIGIN_LABEL_KEYS[origin]))
    .join(', ');

  const handleTargetChange = useCallback(
    (matchLocation: RuleFormMatchLocation) => {
      setRuleForm((current) => {
        const nextMatchType = ensureValidMatchType({
          matchLocation,
          matchType: current.matchType,
        });

        return {
          ...current,
          matchLocation,
          matchType: nextMatchType,
        };
      });
    },
    [setRuleForm],
  );

  const handleApplyToChange = useCallback(
    (values: string[]) => {
      const applyTo = MATCH_REPLACE_ORIGINS.filter((origin) =>
        values.includes(origin),
      );

      setRuleForm((current) => ({ ...current, applyTo }));
    },
    [setRuleForm],
  );

  return (
    <VStack gap="3" alignItems="flex-start" w="full">
      <SegmentGroup.Root
        size="sm"
        value={ruleForm.matchLocation}
        orientation="horizontal"
        w="full"
        onValueChange={(details) => {
          if (!details.value) {
            return;
          }

          handleTargetChange(details.value as RuleFormMatchLocation);
        }}
      >
        <SegmentGroup.Indicator bg="bg.muted" />
        <SegmentGroup.Item
          value="request"
          flex="1"
          py="1"
          h="8"
          display="flex"
          alignItems="center"
          justifyContent="center"
          _hover={{ bg: 'bg.hover' }}
        >
          <SegmentGroup.ItemText fontSize="xs">
            {tRuleDialog('request')}
          </SegmentGroup.ItemText>
          <SegmentGroup.ItemHiddenInput />
        </SegmentGroup.Item>
        <SegmentGroup.Item
          value="response"
          flex="1"
          py="1"
          h="8"
          display="flex"
          alignItems="center"
          justifyContent="center"
          _hover={{ bg: 'bg.hover' }}
        >
          <SegmentGroup.ItemText fontSize="xs">
            {tRuleDialog('response')}
          </SegmentGroup.ItemText>
          <SegmentGroup.ItemHiddenInput />
        </SegmentGroup.Item>
        <SegmentGroup.Item
          value="both"
          flex="1"
          py="1"
          h="8"
          display="flex"
          alignItems="center"
          justifyContent="center"
          _hover={{ bg: 'bg.hover' }}
        >
          <SegmentGroup.ItemText fontSize="xs">
            {tRuleDialog('both')}
          </SegmentGroup.ItemText>
          <SegmentGroup.ItemHiddenInput />
        </SegmentGroup.Item>
      </SegmentGroup.Root>

      <Field.Root w="full" invalid={!ruleForm.applyTo.length} required>
        <VStack gap="1" alignItems="flex-start" w="full">
          <LabelDefault color="fg.muted">
            {tRuleDialog('applyRuleTo')}
          </LabelDefault>
          <Select.Root
            multiple
            collection={originCollection}
            value={ruleForm.applyTo}
            positioning={{ strategy: 'fixed', sameWidth: true }}
            onValueChange={(details) => {
              handleApplyToChange(details.value);
            }}
          >
            <Select.HiddenSelect />
            <Select.Control w="full">
              <Select.Trigger h={RULE_EDITOR_CONTROL_HEIGHT} w="full">
                <Select.ValueText fontSize="xs" truncate>
                  {selectedOriginsLabel}
                </Select.ValueText>
                <Select.Indicator>
                  <ArrowUpDownIcon size={12} />
                </Select.Indicator>
              </Select.Trigger>
            </Select.Control>
            <Select.Positioner>
              <Select.Content maxH="240px" overflowY="auto">
                {originCollection.items.map((item) => {
                  const origin = MATCH_REPLACE_ORIGINS.find(
                    (candidate) => candidate === item.value,
                  );

                  if (!origin) {
                    return null;
                  }

                  return (
                    <Tooltip
                      key={origin}
                      title={tRuleDialog(ORIGIN_TOOLTIP_KEYS[origin])}
                      positioning={{ placement: 'right' }}
                      openDelay={300}
                    >
                      <Select.Item item={item}>
                        <Select.ItemText fontSize="xs">
                          {item.label}
                        </Select.ItemText>
                        <Select.ItemIndicator>
                          <Tick01Icon size={16} />
                        </Select.ItemIndicator>
                      </Select.Item>
                    </Tooltip>
                  );
                })}
              </Select.Content>
            </Select.Positioner>
          </Select.Root>
          <Field.ErrorText>{tRuleDialog('applyToRequired')}</Field.ErrorText>
        </VStack>
      </Field.Root>
    </VStack>
  );
}
